import { useAdminListingsHealth } from '@/hooks/useAdminListingsHealth'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { ADMIN_PORTAL_PATH } from '@/lib/adminPortal'

export default function AdminListingsHealthPage() {
  const { data, isLoading, error } = useAdminListingsHealth()

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-4">
        <p style={{ color: 'var(--text-secondary)' }}>Loading listings health…</p>
      </div>
    )
  }

  if (error) {
    return (
      <section aria-labelledby="listings-health-error-heading">
        <h1 id="listings-health-error-heading" className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Listings health
        </h1>
        <p role="alert" style={{ color: 'var(--color-error)' }}>
          Failed to load: {error instanceof Error ? error.message : String(error)}.
        </p>
      </section>
    )
  }

  const health = data?.health ?? null
  const channels = data?.channels ?? []

  const coverageMin =
    health?.coverage_min_date != null
      ? format(new Date(health.coverage_min_date + 'Z'), 'dd MMM yyyy')
      : '—'
  const coverageMax =
    health?.coverage_max_date != null
      ? format(new Date(health.coverage_max_date + 'Z'), 'dd MMM yyyy')
      : '—'
  const lastSuccess =
    health?.last_success_at != null
      ? format(new Date(health.last_success_at), 'dd MMM yyyy, HH:mm')
      : '—'
  const lastRunType = health?.last_run_trigger_type ?? '—'
  const lastRunTime =
    health?.last_run_started_at != null
      ? format(new Date(health.last_run_started_at), 'dd MMM yyyy, HH:mm')
      : '—'
  const lastError = health?.last_run_error ?? health?.last_error ?? null
  const nextRunLabel =
    health?.next_run_at != null
      ? format(new Date(health.next_run_at), 'dd MMM yyyy, HH:mm')
      : 'Not scheduled'
  const successRate =
    health && health.total_runs_last_10 > 0
      ? `${health.success_count_last_10}/${health.total_runs_last_10} (${Math.round((100 * health.success_count_last_10) / health.total_runs_last_10)}%)`
      : '—'

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <h1 id="listings-health-heading" className="text-3xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
        Listings health
      </h1>

      {/* Coverage */}
      <section
        className="mb-6 p-4 rounded border-2"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
        aria-labelledby="coverage-heading"
      >
        <h2 id="coverage-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Coverage
        </h2>
        <dl className="grid gap-2 sm:grid-cols-2">
          <div>
            <dt className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
              Date range
            </dt>
            <dd style={{ color: 'var(--text-primary)' }}>
              {coverageMin} – {coverageMax}
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
              Forward coverage
            </dt>
            <dd style={{ color: 'var(--text-primary)' }}>
              {health?.forward_coverage_days ?? '—'} days
            </dd>
          </div>
        </dl>
        <p className="mt-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <Link
            to={`${ADMIN_PORTAL_PATH}/listings-report`}
            className="underline focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ color: 'var(--color-focus)' }}
          >
            Listings report
          </Link>{' '}
          — counts by day, channel, and accessibility.{' '}
          <Link
            to={`${ADMIN_PORTAL_PATH}/listings-management`}
            className="underline focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ color: 'var(--color-focus)' }}
          >
            Listings control
          </Link>{' '}
          — sync and schedule.{' '}
          <Link
            to={`${ADMIN_PORTAL_PATH}/listings-channels`}
            className="underline focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ color: 'var(--color-focus)' }}
          >
            Channel control
          </Link>{' '}
          — show or hide channels on TV Listings.
        </p>
      </section>

      {/* Ingestion */}
      <section
        className="mb-6 p-4 rounded border-2"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
        aria-labelledby="ingestion-heading"
      >
        <h2 id="ingestion-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Ingestion
        </h2>
        <dl className="grid gap-2 sm:grid-cols-2">
          <div>
            <dt className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
              Last successful import
            </dt>
            <dd style={{ color: 'var(--text-primary)' }}>{lastSuccess}</dd>
          </div>
          <div>
            <dt className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
              Last run
            </dt>
            <dd style={{ color: 'var(--text-primary)' }}>
              {lastRunType} • {lastRunTime}
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
              Next run
            </dt>
            <dd style={{ color: 'var(--text-primary)' }}>{nextRunLabel}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
              Success rate (last 10 runs)
            </dt>
            <dd style={{ color: 'var(--text-primary)' }}>{successRate}</dd>
          </div>
          {lastError && (
            <div className="sm:col-span-2">
              <dt className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
                Last error
              </dt>
              <dd
                className="mt-1 p-2 rounded text-sm"
                style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                role="alert"
              >
                {lastError}
              </dd>
            </div>
          )}
        </dl>
      </section>

      {/* Per-channel */}
      <section
        className="p-4 rounded border-2"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
        aria-labelledby="per-channel-heading"
      >
        <h2 id="per-channel-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Per-channel
        </h2>
        {channels.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No channels in listing.</p>
        ) : (
          <div className="overflow-x-auto">
            <table
              className="w-full border-collapse"
              style={{ borderColor: 'var(--bg-tertiary)' }}
              aria-describedby="per-channel-heading"
            >
              <caption className="sr-only">
                Channels from listing with display status and gap status
              </caption>
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="text-left py-2 px-3 border border-solid"
                    style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  >
                    Channel
                  </th>
                  <th
                    scope="col"
                    className="text-left py-2 px-3 border border-solid"
                    style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  >
                    Displayed
                  </th>
                  <th
                    scope="col"
                    className="text-left py-2 px-3 border border-solid"
                    style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {channels.map((row) => (
                  <tr key={row.channel_id}>
                    <td
                      className="py-2 px-3 border border-solid"
                      style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    >
                      {row.channel_name}
                    </td>
                    <td
                      className="py-2 px-3 border border-solid"
                      style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    >
                      {row.is_displayed ? 'Yes' : 'No'}
                    </td>
                    <td
                      className="py-2 px-3 border border-solid"
                      style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    >
                      {row.gap_status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
